import "./EditableGridCell.css";
import * as React from "react";
import { Dictionary, EntityType, IBaseEntity } from "../../../entities/common";
import { IValidator } from "../../../validation";
import { Field } from "../../../entities/Metadata";
import { getId } from "office-ui-fabric-react";
import ErrorMessageCallout from "./ErrorMessageCallout";
import CellEditor from "./CellEditor";
import CellFormatter from "./CellFormatter";

type FieldCustomProps = {
    customFieldElementRender?: Dictionary<any>,
    customFieldValidatorBuilder?: Dictionary<(state: IBaseEntity, field: Field) => IValidator>
};

export type GridCellProps<T extends IBaseEntity> = {
    item: T,
    field: Field,
    entityType?: EntityType,
    onEditComplete: (value: any, extraUpdates?: Dictionary<any>) => Promise<void>,
    withEditIcon?: boolean,
    className?: string,
    readonly?: boolean,
    disableNavigation?: boolean,
    isTimelineView: boolean,
    openInPopup?: boolean,
    onEditModeChanged?: (inEdit: boolean) => void,
    getItemValue: (item: T, field: Field) => any,
    applyItemUpdates: (item: T, field: Field, value: any, extraUpdates?: Dictionary<any>) => T
} & FieldCustomProps;

const ERROR_MESSAGE_DELAY = 3000;
const SAVE_FAILED_MESSAGE = 'Failed to save changes. Please try again.';

export const EditableGridCell = <T extends IBaseEntity>(props: GridCellProps<T>) => {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [item, setItem] = React.useState(props.item);
    const [error, setError] = React.useState<string | undefined>();
    // newest item from the store; read when a save settles, as props are stale inside the promise callback
    const incomingItem = React.useRef(props.item);
    incomingItem.current = props.item;
    // item the save started from, to tell whether the store has published anything since
    const itemBeforeSave = React.useRef(props.item);
    // an edit is applied locally and still waiting for the server
    const isSaving = React.useRef(false);
    const latestToken = React.useRef(0);
    const isMountedRef = React.useRef(true);
    React.useEffect(() => () => { isMountedRef.current = false; }, []);

    React.useEffect(() => {
        // while editing, or while the applied value waits for confirmation, the local value wins
        if (isEditing || isSaving.current) {
            return;
        }

        setItem(props.item);
    }, [props.item, isEditing])
    const controlId = React.useMemo(() => getId(), []);

    const onSaveSettled = (token: number, errorMessage?: string) => {
        if (!isMountedRef.current || token !== latestToken.current) {
            // unmounted, or a newer edit already owns the cell
            return;
        }

        isSaving.current = false;
        if (errorMessage) {
            // the save is lost: reset to the source of truth, which still holds the pre-edit value
            setItem(incomingItem.current);
            setError(errorMessage);
            setTimeout(() => isMountedRef.current && setError(undefined), ERROR_MESSAGE_DELAY);
        } else if (incomingItem.current !== itemBeforeSave.current) {
            // saved, and the store already published an item: it is the source of truth now
            setItem(incomingItem.current);
        }
        // saved, but the store update has not reached this cell yet (grid rows re-render asynchronously):
        // keep the value applied locally - resetting to the item still in props is what makes it blink
        // new -> old -> new. The effect above adopts the published item as soon as it arrives.
    }

    return isEditing
        ? <CellEditor {...props}
            item={item}
            onClose={(err?: string) => {
                err && setError(err);
                setIsEditing(false);
                props.onEditModeChanged?.(false);
                setTimeout(() => setError(undefined), ERROR_MESSAGE_DELAY);
            }}
            onEditComplete={(val: any, extraUpdates?: Dictionary<any>) => {
                // show the new value right away and hold it until the server confirms or rejects it
                setItem(props.applyItemUpdates(item, props.field, val, extraUpdates));
                latestToken.current += 1;
                const token = latestToken.current;
                itemBeforeSave.current = props.item;
                isSaving.current = true;
                return props.onEditComplete(val, extraUpdates)
                    .then(() => onSaveSettled(token), () => onSaveSettled(token, SAVE_FAILED_MESSAGE));
            }}
        />
        : <>
            <CellFormatter {...props}
                controlId={controlId}
                item={item}
                value={props.getItemValue(item, props.field)}
                onEditClick={() => {
                    setIsEditing(true);
                    props.onEditModeChanged?.(true);
                }} />
            {!!error && <ErrorMessageCallout controlId={controlId} error={error} />}
        </>;
}
